/* ============================================================
   SAM-1237 — location of a business: pure form state and payload rules (canvas v306, backend crud #884).

   internal ("Cap Cana", the tenant's name): an estate group, optionally a building of it and a unit or shop;
   external: address, city, zip code (optional), country (ISO alpha-2); virtual: nothing.
   `type ''` is a business whose location was never set (every business before this ticket).
   ============================================================ */

export type LocationType = 'internal' | 'external' | 'virtual';

export interface LocationForm {
  type: LocationType | '';
  groupId: number | null;
  buildingId: number | null;
  unitId: number | null;
  address: string;
  city: string;
  zip: string;
  country: string;
}

export const EMPTY_LOCATION: LocationForm = {
  type: '', groupId: null, buildingId: null, unitId: null, address: '', city: '', zip: '', country: '',
};

/** What a new business starts with: Cap Cana selected, as in the mock. */
export const NEW_BUSINESS_LOCATION: LocationForm = { ...EMPTY_LOCATION, type: 'internal' };

/** The location columns of a `business` row as the API returns them. */
export interface LocationColumns {
  location_type?: string | null;
  building_id?: number | string | null;
  estate_id?: number | string | null;
  address?: string | null;
  city?: string | null;
  zip_code?: string | null;
  country?: string | null;
}

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function locationFromBusiness(row: LocationColumns | null | undefined, primaryGroupId: number | null): LocationForm {
  const type = row?.location_type;
  if (type !== 'internal' && type !== 'external' && type !== 'virtual') return { ...EMPTY_LOCATION };
  return {
    type,
    groupId: type === 'internal' ? primaryGroupId : null,
    buildingId: type === 'internal' ? num(row?.building_id) : null,
    unitId: type === 'internal' ? num(row?.estate_id) : null,
    address: type === 'external' ? (row?.address ?? '') : '',
    city: type === 'external' ? (row?.city ?? '') : '',
    zip: type === 'external' ? (row?.zip_code ?? '') : '',
    country: type === 'external' ? (row?.country ?? '') : '',
  };
}

export type LocationIssue = 'type' | 'group' | 'address' | 'city' | 'country';

/** What still blocks saving. Internal needs its estate group; external needs address, city and country. */
export function locationIssues(form: LocationForm): LocationIssue[] {
  if (form.type === '') return ['type'];
  if (form.type === 'internal') return form.groupId == null ? ['group'] : [];
  if (form.type === 'external') {
    const issues: LocationIssue[] = [];
    if (!form.address.trim()) issues.push('address');
    if (!form.city.trim()) issues.push('city');
    if (!form.country.trim()) issues.push('country');
    return issues;
  }
  return [];
}

/** Normalised copy used to compare two forms (what is stored ignores blanks and case of the country). */
const norm = (f: LocationForm) => ({
  type: f.type,
  groupId: f.type === 'internal' ? f.groupId : null,
  buildingId: f.type === 'internal' ? f.buildingId : null,
  unitId: f.type === 'internal' ? f.unitId : null,
  address: f.type === 'external' ? f.address.trim() : '',
  city: f.type === 'external' ? f.city.trim() : '',
  zip: f.type === 'external' ? f.zip.trim() : '',
  country: f.type === 'external' ? f.country.trim().toUpperCase() : '',
});

export const sameLocation = (a: LocationForm, b: LocationForm): boolean =>
  JSON.stringify(norm(a)) === JSON.stringify(norm(b));

/** The `business` columns that hold this location, every one set (nulls clear what the type does not use). */
export function locationColumns(form: LocationForm): Record<string, unknown> {
  const n = norm(form);
  if (form.type === 'internal') {
    return { location_type: 'internal', building_id: n.buildingId, estate_id: n.unitId, address: null, city: null, zip_code: null, country: null };
  }
  if (form.type === 'external') {
    return { location_type: 'external', building_id: null, estate_id: null, address: n.address, city: n.city, zip_code: n.zip || null, country: n.country };
  }
  if (form.type === 'virtual') {
    return { location_type: 'virtual', building_id: null, estate_id: null, address: null, city: null, zip_code: null, country: null };
  }
  return {};
}

/** The same for POST /business: only what the type uses. */
export function locationCreateColumns(form: LocationForm): Record<string, unknown> {
  const all = locationColumns(form);
  return Object.fromEntries(Object.entries(all).filter(([, v]) => v !== null && v !== undefined && v !== ''));
}

/** A readable summary of a location for the read view and the preview. */
export function locationSummary(
  form: LocationForm,
  names: { tenant: string; group?: string; building?: string; unit?: string; country?: string },
): string {
  if (form.type === 'internal') return [names.group, names.building, names.unit].filter(Boolean).join(' · ');
  if (form.type === 'external') return [form.address.trim(), form.city.trim()].filter(Boolean).join(' · ');
  return '';
}
